import { useMemo } from 'react'
import { useFloorplanStore, useModelStore } from '@editor/stores'
import usePositionVisibilityLookup from './usePositionVisibilityLookup'

/**
 * Per-guid "is this hidden in the floorplan" lookup: rips/lintels follow
 * `positionVisibilityLookup`, and anything in a `useHideFloorplanElements`
 * group (e.g. the element currently being edited) is force-hidden. Shared by
 * `FloorplanScene` (mesh + label rendering) and `PlanLabelController` (which
 * needs the same predicate to build a selected label's own descriptor).
 */
const useFloorplanGuidToHidden = (): Record<string, boolean> => {
  const model = useModelStore(state => state.model)
  const positionVisibilityLookup = usePositionVisibilityLookup()
  const hiddenFloorplanElementIdGroups = useFloorplanStore(state => state.hiddenElementIdGroups)

  return useMemo(() => {
    const hidden: Record<string, boolean> = {}

    // Find all rips and lintels that belong to this wall and should be hidden
    const allPositions = [...(model.rips || []), ...(model.lintels || [])]
    allPositions.forEach(position => {
      hidden[position.position_guid] = !positionVisibilityLookup[position.position_guid]
    })

    Object.values(hiddenFloorplanElementIdGroups)
      .flat()
      .forEach(guid => {
        hidden[guid] = true
      })

    return hidden
  }, [positionVisibilityLookup, model, hiddenFloorplanElementIdGroups])
}

export default useFloorplanGuidToHidden
